import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { invalidateFeatureFlags } from "../../lib/featureFlags";
import { toast } from "sonner";
import {
  ToggleLeft,
  ToggleRight,
  CreditCard,
  Eye,
  Lightning,
  ChartLineUp,
  PlugsConnected,
  CloudSun,
  Info,
} from "@phosphor-icons/react";

const GROUP_META = {
  subscription: { label: "Subscription", icon: CreditCard, tone: "text-emerald-500" },
  visibility:   { label: "Visibility",   icon: Eye,        tone: "text-primary" },
  boost:        { label: "Boost",        icon: Lightning,  tone: "text-amber-500" },
  analytics:    { label: "Analytics",    icon: ChartLineUp,tone: "text-sky-500" },
  integrations: { label: "Integrations", icon: PlugsConnected, tone: "text-violet-500" },
  weather:      { label: "Weather",      icon: CloudSun,   tone: "text-sky-400" },
};

const GROUP_ORDER = ["subscription", "visibility", "boost", "analytics", "integrations", "weather"];

export default function AdminFeatureFlagsCard() {
  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingKey, setPendingKey] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/feature-flags");
      setFlags(res.data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load flags");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const groups = useMemo(() => {
    const out = {};
    for (const f of flags) {
      (out[f.group] = out[f.group] || []).push(f);
    }
    return out;
  }, [flags]);

  const toggle = async (flag) => {
    const nextValue = !flag.value;
    // optimistic flip
    setFlags((prev) => prev.map((f) => (f.key === flag.key ? { ...f, value: nextValue } : f)));
    setPendingKey(flag.key);
    try {
      await api.put(`/admin/feature-flags/${flag.key}`, { value: nextValue });
      invalidateFeatureFlags();
      toast.success(`${flag.label} turned ${nextValue ? "ON" : "OFF"} — change is live instantly`);
    } catch (e) {
      // revert on failure
      setFlags((prev) => prev.map((f) => (f.key === flag.key ? { ...f, value: !nextValue } : f)));
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't toggle flag");
    } finally {
      setPendingKey(null);
    }
  };

  return (
    <section
      className="rounded-2xl border border-border bg-card p-5 sm:p-6"
      data-testid="admin-feature-flags-card"
    >
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-primary font-semibold">
            <ToggleRight weight="fill" className="h-3.5 w-3.5" />
            Feature Flags
          </div>
          <h2 className="mt-1 font-display font-bold text-2xl tracking-tight">
            Monetization &amp; visibility controls
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Toggle features ON/OFF instantly — no redeploy. Founding Pro owners keep access regardless.
          </p>
        </div>
      </header>

      {loading ? (
        <div className="mt-5 px-3 py-8 text-center text-sm text-muted-foreground">
          Loading flags…
        </div>
      ) : (
        <div className="mt-5 space-y-5">
          {GROUP_ORDER.filter((g) => groups[g]).map((g) => {
            const meta = GROUP_META[g] || { label: g, icon: Info, tone: "text-muted-foreground" };
            return (
              <div key={g} data-testid={`ff-group-${g}`}>
                <div className="flex items-center gap-2 mb-2">
                  <meta.icon weight="duotone" className={"h-4 w-4 " + meta.tone} />
                  <h3 className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
                    {meta.label}
                  </h3>
                </div>
                <div className="rounded-xl border border-border divide-y divide-border overflow-hidden">
                  {groups[g].map((flag) => (
                    <FlagRow
                      key={flag.key}
                      flag={flag}
                      pending={pendingKey === flag.key}
                      onToggle={() => toggle(flag)}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

function FlagRow({ flag, pending, onToggle }) {
  return (
    <div className="px-3 py-3 flex items-start gap-3" data-testid={`ff-row-${flag.key}`}>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium leading-tight">{flag.label}</div>
        <div className="text-[11px] text-muted-foreground mt-0.5 leading-snug">
          {flag.desc}
        </div>
      </div>
      <button
        type="button"
        onClick={onToggle}
        disabled={pending}
        role="switch"
        aria-checked={flag.value}
        className={
          "relative h-7 w-12 rounded-full transition-colors flex-none mt-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary " +
          (flag.value ? "bg-primary" : "bg-muted") +
          (pending ? " opacity-60" : "")
        }
        data-testid={`ff-toggle-${flag.key}`}
        aria-label={`${flag.label} is ${flag.value ? "ON" : "OFF"}`}
      >
        <span
          className={
            "absolute top-0.5 h-6 w-6 rounded-full bg-white shadow transition-transform " +
            (flag.value ? "translate-x-5" : "translate-x-0.5")
          }
        />
      </button>
    </div>
  );
}
